import { useEffect, useState } from 'react';
import { UITrustQuoteStrip } from 'blocks/trust-quote-strip';
import styles from './ui-rolling-reviews.module.scss';

export interface UIRollingReviewsItem {
    id: string;
    source: 'Google' | 'Trustpilot';
    quote: string;
    name: string;
}

interface UIRollingReviewsProps {
    reviews: UIRollingReviewsItem[];
    /** Milliseconds each review stays up before rolling to the next. */
    intervalMs?: number;
    className?: string;
}

/** Matches the CSS roll-out/roll-in animation-duration in the module stylesheet. */
const ROLL_MS = 700;

/**
 * Barrel-roll testimonial rotator — a split-flap-style flip, not a fade. The
 * outgoing quote rotates up and away around its top edge while the next one
 * rotates down into the same spot from behind it, both sharing an axis so
 * they read as one drum turning rather than two unrelated animations.
 *
 * Built on top of UITrustQuoteStrip so it inherits that block's exact
 * quote/author styling; this component only owns the rotation timer and the
 * flip choreography.
 *
 * Paused on hover/focus so a reader mid-quote doesn't get cut off, and the
 * flip is skipped entirely under prefers-reduced-motion (CSS-level, see the
 * module stylesheet) rather than left running for users who've opted out of
 * motion.
 */
export function UIRollingReviews({
    reviews,
    intervalMs = 3200,
    className = '',
}: UIRollingReviewsProps) {
    const [index, setIndex] = useState(0);
    const [flipping, setFlipping] = useState(false);
    const [paused, setPaused] = useState(false);

    useEffect(() => {
        if (paused || reviews.length <= 1) return undefined;
        const timer = setInterval(() => {
            setIndex((i) => (i + 1) % reviews.length);
            setFlipping(true);
        }, intervalMs);
        return () => clearInterval(timer);
    }, [paused, reviews.length, intervalMs]);

    // The outgoing card only needs to exist in the DOM for the flip's
    // duration — dropping it afterward keeps a paused/hovered stage at rest
    // with a single layer instead of two permanently-stacked ones.
    useEffect(() => {
        if (!flipping) return undefined;
        const timeout = setTimeout(() => setFlipping(false), ROLL_MS);
        return () => clearTimeout(timeout);
    }, [flipping]);

    if (reviews.length === 0) return null;
    const review = reviews[index % reviews.length];
    const previousIndex = (index - 1 + reviews.length) % reviews.length;
    const previousReview = reviews[previousIndex];

    const badge = (r: UIRollingReviewsItem) => ({
        verifiedBadge:
            r.source === 'Google'
                ? { src: '/google-5-stars.png', alt: 'Google, 5 stars' }
                : { src: '/trustpilot-five-stars.png', alt: 'Trustpilot, 5 stars' },
    });

    return (
        <div
            data-slot="rolling-reviews"
            className={[styles.stage, className].filter(Boolean).join(' ')}
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
            onFocus={() => setPaused(true)}
            onBlur={() => setPaused(false)}
        >
            {flipping && (
                <div
                    key={`out-${previousReview.id}`}
                    className={styles.cardOut}
                    aria-hidden="true"
                >
                    <UITrustQuoteStrip
                        quote={previousReview.quote}
                        author={{ name: previousReview.name, ...badge(previousReview) }}
                    />
                </div>
            )}
            <div key={`in-${review.id}`} className={styles.cardIn}>
                <UITrustQuoteStrip
                    quote={review.quote}
                    author={{ name: review.name, ...badge(review) }}
                />
            </div>
        </div>
    );
}

export default UIRollingReviews;
